import { useEffect, useState } from 'react';
import { createPurpose, getPurpose, patchPurpose, type Purpose, type PurposeInput } from './api';
import { draftKey, readDraft, removeDraft, writeDraft } from './drafts';
const empty: PurposeInput = { code: '', label: '', noteTypes: [] };
type FormDraft = { value: PurposeInput; revision: number | null };
function restored(value: unknown): value is FormDraft {
  if (typeof value !== 'object' || value === null || !('value' in value)) return false;
  const form = value.value;
  return (
    typeof form === 'object' &&
    form !== null &&
    'code' in form &&
    typeof form.code === 'string' &&
    'label' in form &&
    typeof form.label === 'string' &&
    'noteTypes' in form &&
    Array.isArray(form.noteTypes) &&
    form.noteTypes.every((v) => v === 'travel' || v === 'reimbursement') &&
    'revision' in value &&
    (value.revision === null || typeof value.revision === 'number')
  );
}
export function usePurposeEditor(id: string, saved: () => void) {
  const [purpose, setPurpose] = useState<Purpose | null>(null);
  const [value, setValue] = useState<PurposeInput>(empty);
  const [revision, setRevision] = useState<number | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const key = draftKey(id);
  useEffect(() => {
    let live = true;
    setReady(false);
    setError('');
    const fill = (p: Purpose | null) => {
      const draft = readDraft(key);
      setPurpose(p);
      let initial = empty;
      if (p) initial = { code: p.code, label: p.label, noteTypes: p.noteTypes };
      setValue(restored(draft) ? draft.value : initial);
      setRevision(restored(draft) ? draft.revision : (p?.revision ?? null));
      if (restored(draft) && p && draft.revision !== p.revision) setError('CONFIGURATION_CHANGED');
      setReady(true);
    };
    if (id === 'new') fill(null);
    else
      void getPurpose(id).then((outcome) => {
        if (!live) return;
        if (outcome.ok) fill(outcome.value);
        else setError(outcome.code);
      });
    return () => {
      live = false;
    };
  }, [id, key, retry]);
  const valid = validPurpose(value);
  const changed =
    !purpose ||
    value.label.trim() !== purpose.label ||
    [...value.noteTypes].sort().join() !== [...purpose.noteTypes].sort().join();
  const change = (next: PurposeInput) => {
    setValue(next);
    writeDraft(key, { value: next, revision });
  };
  const save = usePurposeSave({
    id,
    key,
    value,
    revision,
    ready,
    busy,
    valid,
    setBusy,
    setError,
    setPurpose,
    setRevision,
    setValue,
    saved,
  });
  const useLatest = () => {
    if (!purpose) return;
    setRevision(purpose.revision);
    writeDraft(key, { value, revision: purpose.revision });
    setError('');
  };
  return {
    purpose,
    value,
    revision,
    ready,
    error,
    busy,
    valid,
    changed,
    change,
    save,
    useLatest,
    reload: () => setRetry((n) => n + 1),
  };
}
type SaveState = {
  id: string;
  key: string;
  value: PurposeInput;
  revision: number | null;
  ready: boolean;
  busy: boolean;
  valid: boolean;
  setBusy: (v: boolean) => void;
  setError: (v: string) => void;
  setPurpose: (v: Purpose) => void;
  setRevision: (v: number) => void;
  setValue: (v: PurposeInput) => void;
  saved: () => void;
};
function usePurposeSave(s: SaveState) {
  return async (active?: boolean) => {
    if (s.busy || !s.ready || (!s.valid && active === undefined)) return false;
    s.setBusy(true);
    s.setError('');
    const outcome =
      s.id === 'new'
        ? await createPurpose({ ...s.value, label: s.value.label.trim() })
        : await patchPurpose(s.id, {
            revision: s.revision ?? 1,
            ...(active === undefined
              ? { label: s.value.label.trim(), noteTypes: s.value.noteTypes }
              : { active }),
          });
    if (!outcome.ok) {
      s.setError(outcome.code);
      if (outcome.code === 'CONFIGURATION_CHANGED') {
        const latest = await getPurpose(s.id);
        if (latest.ok) s.setPurpose(latest.value);
      }
      s.setBusy(false);
      return false;
    }
    s.setBusy(false);
    removeDraft(s.key);
    s.setPurpose(outcome.value);
    s.setRevision(outcome.value.revision);
    s.setValue({
      code: outcome.value.code,
      label: outcome.value.label,
      noteTypes: outcome.value.noteTypes,
    });
    s.saved();
    return true;
  };
}

function validPurpose(value: PurposeInput) {
  return (
    /^[A-Za-z0-9_-]{1,20}$/.test(value.code) && !!value.label.trim() && value.noteTypes.length > 0
  );
}
